import { Space, Spin, Table, Tag, Tooltip } from 'antd'
import Button from 'components/Button'
import Control from 'components/Control/Control'
import Drawer from 'components/Drawer'
import FileReader from 'components/Uploader/FileReader'
import Uploadloader from 'components/Uploader/Uploader'
import {
  BoldText,
  ContainerDiv,
  ErrorMessage,
  FooterFlex,
  Form,
  InfoSection,
  LightText,
  SampleDocContainer,
} from 'modules/Students/Partials/StudentsBulkUpload/Style/style'
import moment from 'moment'
import React, { Fragment, useEffect, useState } from 'react'
import { useSelector } from 'react-redux'
import ErrorMessageToast from 'utils/ErrorMessage'
import instRequest from 'utils/instRequest'
import SuccessMessage from 'utils/SuccessMessage'

// Institute bulk upload: the campus uploads its own free-form student export
// (.xlsx) to S3, then hands the S3 link to institute-node, which forwards it to
// the normalization pipeline. Students are created + emailed a temp password
// asynchronously — so after upload we open a status drawer that surfaces the
// per-batch processing status.
const StudentsBulkUpload = ({
  uploadDrawer,
  setUploadDrawer,
  statusDrawer,
  setStatusDrawer,
  getStudentsListData,
}) => {
  const instituteCampusId = useSelector(
    state => state.auth.instituteDetails.id
  )
  const accessLevel = useSelector(
    state => state.auth.instituteDetails?.instituteSubscription?.accessLevel
  )
  const [file, setFile] = useState('')
  const [fileName, setFileName] = useState('')
  const [isUploaded, setIsUploaded] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [batches, setBatches] = useState([])
  const [loadingBatches, setLoadingBatches] = useState(false)
  const [bulkUploadForm] = Form.useForm()

  // A fresh S3 key per drawer open keeps uploads from colliding.
  useEffect(() => {
    if (uploadDrawer) {
      setFileName(`students/erpupload/${instituteCampusId}_${moment().unix()}`)
      setFile('')
      setIsUploaded(false)
    }
  }, [uploadDrawer])

  // Fetch batches whenever the status drawer is opened.
  useEffect(() => {
    if (statusDrawer) {
      fetchBatches()
    }
  }, [statusDrawer])

  useEffect(() => {
    if (file !== '') setIsLoading(false)
  }, [file])

  const fetchBatches = async () => {
    setLoadingBatches(true)
    try {
      const res = await instRequest.get(
        `/institutes/instituteCampus/${instituteCampusId}/erp-upload/batches?page=1&limit=20`
      )
      setBatches(res?.data?.data?.data || [])
    } catch (error) {
      // Non-fatal: status list just stays empty.
    } finally {
      setLoadingBatches(false)
    }
  }

  const onFileUploaded = uploaded => {
    setFile(uploaded)
    setIsUploaded(true)
  }

  const onSubmitFormData = async () => {
    if (!file?.url) {
      setIsUploaded(false)
      return
    }
    setSubmitting(true)
    try {
      await instRequest.post(
        `/institutes/instituteCampus/${instituteCampusId}/erp-upload`,
        { excelLink: file.url, accessLevel }
      )
      SuccessMessage(
        'File queued. Students will be created and emailed once processing completes.'
      )
      setFile('')
      setIsUploaded(false)
      setUploadDrawer(false)
      setStatusDrawer(true)
      getStudentsListData && getStudentsListData()
    } catch (error) {
      ErrorMessageToast(
        error?.response?.data?.message || 'Upload failed. Please try again.'
      )
    } finally {
      setSubmitting(false)
    }
  }

  const statusColumns = [
    {
      title: 'File',
      dataIndex: 'source_file',
      key: 'source_file',
      width: 200,
      ellipsis: {
        showTitle: false,
      },
      render: value =>
        value ? (
          <Tooltip placement="topLeft" title={value}>
            {value}
          </Tooltip>
        ) : (
          '—'
        ),
    },
    {
      title: 'Uploaded',
      dataIndex: 'updated_at',
      key: 'updated_at',
      render: value => (value ? moment(value).format('DD MMM YYYY, HH:mm') : '—'),
    },
    {
      title: 'Total',
      dataIndex: 'total_rows',
      key: 'total_rows',
      align: 'center',
    },
    {
      title: 'Done',
      dataIndex: 'completed_count',
      key: 'completed_count',
      align: 'center',
      render: value => <Tag color="green">{value ?? 0}</Tag>,
    },
    {
      title: 'Pending',
      dataIndex: 'pending_count',
      key: 'pending_count',
      align: 'center',
      render: value => <Tag color="gold">{value ?? 0}</Tag>,
    },
    {
      title: 'In Progress',
      dataIndex: 'in_progress_count',
      key: 'in_progress_count',
      align: 'center',
      render: value =>
        value ? <Tag color="blue">{value}</Tag> : <Tag>0</Tag>,
    },
    {
      title: 'Failed',
      dataIndex: 'failed_count',
      key: 'failed_count',
      align: 'center',
      render: value =>
        value ? <Tag color="red">{value}</Tag> : <Tag>0</Tag>,
    },
    {
      title: 'Skipped',
      dataIndex: 'skipped_count',
      key: 'skipped_count',
      align: 'center',
      render: value =>
        value ? <Tag color="default">{value}</Tag> : <Tag>0</Tag>,
    },
  ]

  const uploadContent = () => (
    <Fragment>
      <InfoSection>
        <Space direction="vertical" size={2}>
          <SampleDocContainer>
            <BoldText>Upload File</BoldText>
            <LightText>
              Upload your institute student export (.xlsx). Columns are
              auto-detected — no fixed template needed. Students are created and
              emailed a temporary password once processing completes.
            </LightText>
          </SampleDocContainer>
        </Space>
      </InfoSection>

      <Form
        scrollToFirstError
        onFinish={onSubmitFormData}
        id="bulkUpload"
        layout="vertical"
        form={bulkUploadForm}
      >
        <Control column top="20px" center>
          <Uploadloader
            setFile={onFileUploaded}
            isDefault={true}
            fileName={fileName}
            setIsLoading={setIsLoading}
          />
          {!isUploaded && <ErrorMessage>Upload file..</ErrorMessage>}
        </Control>
        {isLoading ? (
          <Spin />
        ) : (
          file && (
            <Control column top="20px" center>
              <FileReader
                file={file}
                setFile={setFile}
                setIsUploaded={setIsUploaded}
              />
            </Control>
          )
        )}
      </Form>
    </Fragment>
  )

  const uploadFooter = () => (
    <FooterFlex right>
      <Button.Secondary text="Cancel" onClick={() => setUploadDrawer(false)} />
      <Button.Primary
        htmlType="submit"
        form="bulkUpload"
        text="Upload"
        loading={submitting}
        disabled={!file?.url || submitting}
      />
    </FooterFlex>
  )

  const statusContent = () => (
    <ContainerDiv>
      <InfoSection>
        <BoldText>Recent uploads</BoldText>
        <Button.Secondary
          text="Refresh"
          onClick={fetchBatches}
          loading={loadingBatches}
        />
      </InfoSection>
      <Table
        size="small"
        rowKey="sheet_id"
        columns={statusColumns}
        dataSource={batches}
        loading={loadingBatches}
        pagination={false}
        locale={{ emptyText: 'No uploads yet' }}
        scroll={{ x: 900 }}
      />
    </ContainerDiv>
  )

  const statusFooter = () => (
    <FooterFlex right>
      <Button.Secondary text="Close" onClick={() => setStatusDrawer(false)} />
      <Button.Secondary
        text="Upload new file"
        onClick={() => {
          setStatusDrawer(false)
          setUploadDrawer(true)
        }}
      />
    </FooterFlex>
  )

  return (
    <>
      {uploadDrawer && (
        <Drawer
          width="45%"
          title={'Bulk Upload'}
          placement="right"
          closable={true}
          visible={uploadDrawer}
          content={uploadContent()}
          footer={uploadFooter()}
          onClose={() => setUploadDrawer(false)}
        />
      )}
      {statusDrawer && (
        <Drawer
          width="45%"
          title={'Upload Status'}
          placement="right"
          closable={true}
          visible={statusDrawer}
          content={statusContent()}
          footer={statusFooter()}
          onClose={() => setStatusDrawer(false)}
        />
      )}
    </>
  )
}

export default StudentsBulkUpload
